import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/services/${id}`);
      setService(response.data);
    } catch (error) {
      console.error('Error fetching service:', error);
      if (error.response?.status === 404) {
        setError('Service not found');
      } else {
        setError('Failed to load service details');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Service Not Found</h4>
          <p>{error || 'The service you are looking for does not exist.'}</p>
          <Link to="/services" className="btn btn-primary">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabels = {
    general_care: 'General Care',
    diagnostic: 'Diagnostic',
    specialized: 'Specialized'
  };

  return (
    <>
      {/* Breadcrumb */}
      <div className="container py-3">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none" style={{ color: '#0066cc' }}>
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/services" className="text-decoration-none" style={{ color: '#0066cc' }}>
                Services
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {service.name}
            </li>
          </ol>
        </nav>
      </div>

      {/* Service Detail Section */}
      <section className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="mb-4" style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1a1a1a' }}>
                {service.name}
              </h1>
              
              {service.alias && (
                <p className="lead mb-4" style={{ fontSize: '1.2rem', color: '#666' }}>
                  Also known as: <strong>{service.alias}</strong>
                </p>
              )}

              {service.image && (
                <div className="mb-4">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="img-fluid rounded"
                    style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {service.description && (
                <div className="mb-4">
                  <h2 className="mb-3" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1a1a1a' }}>
                    About This Service
                  </h2>
                  <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333' }}>
                    {service.description}
                  </p>
                </div>
              )}

              {(service.operating_days || service.operating_hours) && (
                <div className="mb-4">
                  <h2 className="mb-3" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1a1a1a' }}>
                    Operating Hours
                  </h2>
                  {service.operating_days && (
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333' }}>
                      <i className="bi bi-calendar3 me-2" style={{ color: '#0066cc' }}></i>
                      <strong>Days:</strong> {service.operating_days}
                    </p>
                  )}
                  {service.operating_hours && (
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333' }}>
                      <i className="bi bi-clock me-2" style={{ color: '#0066cc' }}></i>
                      <strong>Hours:</strong> {service.operating_hours}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-5">
                <Link to="/contact" className="btn btn-primary btn-lg me-3">
                  Contact us
                </Link>
                <Link to="/services" className="btn btn-outline-primary btn-lg">
                  Back to Services
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-3">Service Information</h5>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <strong>Category:</strong>{' '}
                      <span className="badge bg-info">
                        {categoryLabels[service.category] || service.category}
                      </span>
                    </li>
                  </ul>
                  
                  <div className="mt-4">
                    <h6 className="mb-3">Need Help?</h6>
                    <p className="text-muted small mb-3">
                      Contact us to learn more about this service or to schedule an appointment.
                    </p>
                    <Link to="/contact" className="btn btn-outline-primary btn-sm w-100">
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ServiceDetail;
